import { useEffect, useMemo, useRef } from "react";
import type { Folder } from "~/types/api";
import type { TaskManagerDTO } from "~/types/api-custom";
import { usePathValidation } from "./use-path-validation";
import { useTaskData } from "./use-task-data";
import { useTaskFields } from "./use-task-fields";
import { useTaskFormState } from "./use-task-form-state";
import { useTaskValidation } from "./use-task-validation";

export const useTaskForm = (task?: TaskManagerDTO) => {
  const {
    isEditMode,
    formData,
    setFormData,
    selectedBrandId,
    selectedWorkflowId,
    currentFolderId,
    updateFormField,
  } = useTaskFormState(task);

  const {
    brands,
    workflows,
    folders,
    workflowFields,
    isLoadingFields,
    originalFolderId,
    brandOptions,
    workflowOptions,
    folderOptions,
  } = useTaskData(task, selectedBrandId, selectedWorkflowId);

  const pathCheck = usePathValidation(formData.path, task?.id);

  const { errors, isFormValid, isCheckingPath, validateOnSubmit, clearError } = useTaskValidation(
    formData,
    currentFolderId,
    workflowFields,
    pathCheck
  );

  const { updateTaskFieldValue, getTaskFieldValue, getTaskFieldValueAsArray } = useTaskFields(
    formData,
    workflowFields,
    task,
    setFormData
  );

  const initialFolderId = useMemo(() => {
    if (!folders || !task?.id) return null;
    const folderWithTask = folders.find((f: Folder) =>
      f.folderTasks?.some((ft) => ft.taskId === task.id)
    );
    return folderWithTask?.id ?? null;
  }, [folders, task?.id]);

  const initialPopulatedRef = useRef(false);

  const hasChanges = useMemo(() => {
    if (!isEditMode || !task) return false;
    if ((formData.name || "") !== (task.name || "")) return true;
    if ((formData.path || "") !== (task.path || "")) return true;
    if ((formData.active ?? true) !== (task.active ?? true)) return true;
    if ((formData.brandId || null) !== (task.brandId || null)) return true;
    if ((formData.workflowId || null) !== (task.workflowId || null)) return true;
    if (String(currentFolderId ?? "") !== String(initialFolderId ?? "")) return true;

    const currentFields = (formData.taskFields || []).filter((f) => f.value !== "");
    const initialFields = (task.taskFields || []).filter((f) => f.value !== "");

    if (currentFields.length !== initialFields.length) return true;

    for (let i = 0; i < currentFields.length; i++) {
      const current = currentFields[i];
      if (!current) continue;
      const initial = initialFields.find((f) => f.fieldId === current.fieldId);
      if (!initial || current.value !== initial.value) return true;
    }

    return false;
  }, [formData, task, isEditMode, currentFolderId, initialFolderId]);

  useEffect(() => {
    if (!isEditMode || !folders || !task?.id) return;

    if (!initialPopulatedRef.current && !formData.folder) {
      const folderWithTask = folders.find((f: Folder) =>
        f.folderTasks?.some((ft) => ft.taskId === task.id)
      );
      if (folderWithTask) {
        setFormData((prev) => ({
          ...prev,
          folder: { id: folderWithTask.id, name: folderWithTask.name },
        }));
        initialPopulatedRef.current = true;
      }
    }
  }, [isEditMode, folders, task?.id, formData.folder, setFormData]);

  return {
    isEditMode,
    formData,
    errors,
    hasChanges,
    isFormValid,
    isCheckingPath,
    validateOnSubmit,
    clearError,
    brands,
    workflows,
    folders,
    selectedBrandId,
    selectedWorkflowId,
    currentFolderId,
    workflowFields,
    isLoadingFields,
    originalFolderId,
    brandOptions,
    workflowOptions,
    folderOptions,
    updateFormField,
    updateTaskFieldValue,
    getTaskFieldValue,
    getTaskFieldValueAsArray,
  };
};
